import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import AuthModal from "../components/AuthModal";
import { parseReferralId, storeReferralId } from "../utils/referral";

const AuthModalContext = createContext(null);

export const useAuthModal = () => {
  const context = useContext(AuthModalContext);
  if (!context) {
    throw new Error("useAuthModal must be used within AuthModalProvider");
  }
  return context;
};

export const AuthModalProvider = ({ children }) => {
  const [show, setShow] = useState(false);
  const [formToShow, setFormToShow] = useState("login");
  const [stayOnPage, setStayOnPage] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { isLoggedIn } = useSelector((state) => state.auth);
  const registerRouteModalRef = useRef(false);

  const openAuthModal = useCallback((mode = "login", options = {}) => {
    registerRouteModalRef.current = false;
    setFormToShow(mode);
    setStayOnPage(Boolean(options.stayOnPage));
    setShow(true);
  }, []);

  const closeAuthModal = useCallback(() => {
    setShow(false);
    setStayOnPage(false);
    registerRouteModalRef.current = false;
    if (location.pathname === "/register") {
      navigate("/", { replace: true });
    }
  }, [location.pathname, navigate]);

  useEffect(() => {
    if (location.pathname !== "/register") {
      if (registerRouteModalRef.current) {
        registerRouteModalRef.current = false;
        setShow(false);
        setStayOnPage(false);
      }
      return;
    }

    if (isLoggedIn) {
      registerRouteModalRef.current = false;
      setShow(false);
      navigate("/", { replace: true });
      return;
    }

    const params = new URLSearchParams(location.search);
    const referralId = parseReferralId(params.get("referral_id"));
    if (referralId) storeReferralId(referralId);

    registerRouteModalRef.current = true;
    setFormToShow("register");
    setStayOnPage(false);
    setShow(true);
  }, [location.pathname, location.search, isLoggedIn, navigate]);

  return (
    <AuthModalContext.Provider
      value={{
        openAuthModal,
        closeAuthModal,
        setFormToShow,
        show,
        formToShow,
      }}
    >
      {children}
      <AuthModal
        show={show}
        formToShow={formToShow}
        setFormToShow={setFormToShow}
        modalClose={closeAuthModal}
        stayOnPage={stayOnPage}
      />
    </AuthModalContext.Provider>
  );
};
